import {
  CreateInvestmentAchTransferErrorType,
  useCreateInvestmentAchTransfer,
  type CreateInvestmentAchTransferOutput,
} from '@egw/api'
import { type GQLError } from '@egw/clients'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { Currency } from '@egw/utils'
import { type UseFormSetError } from 'react-hook-form'
import { AMOUNT_KEY } from '../constants'
import { type BackendError, type EBackendError } from '../types/error-types/error-types'
import { TransferSuccessTypes, type TransferFormInterface } from '../types/form-model/form-model'
import { handleGenericBackendErrors, type IGQLErrorResponse } from '../utils'

const log = new Logger('useAchInvestmentTransfer Hook')

interface UseInvestmentAchTransferInterface {
  onSuccess: (_successType: TransferSuccessTypes) => void
  onError: (_errorType: CreateInvestmentAchTransferErrorType | EBackendError) => void
  onInlineError: UseFormSetError<TransferFormInterface>
}

export const useInvestmentAchTransfer = ({ onSuccess, onError, onInlineError }: UseInvestmentAchTransferInterface) => {
  const { t } = useTranslation('features-transfer')

  function onInvestmentAchTransferError(backendError: IGQLErrorResponse | BackendError[] | GQLError): void {
    const errorType = handleGenericBackendErrors({
      backendError,
    })
    onError(errorType)
  }

  function onInvestmentAchTransferSuccess(data: CreateInvestmentAchTransferOutput): void {
    const createInvestmentAchTransfer = data

    // ERROR SCENARIO
    if (
      createInvestmentAchTransfer.__typename === 'CreateInvestmentAchTransferError' ||
      createInvestmentAchTransfer.__typename === 'AnnualRetirementContributionExceededError'
    ) {
      let errorType: CreateInvestmentAchTransferErrorType | 'AnnualRetirementContributionExceededError' | undefined

      if (createInvestmentAchTransfer.__typename === 'CreateInvestmentAchTransferError') {
        errorType = createInvestmentAchTransfer.errorType
      } else {
        errorType = 'AnnualRetirementContributionExceededError'
      }

      switch (errorType) {
        case CreateInvestmentAchTransferErrorType.INSUFFICIENT_FUNDS:
          onInlineError(AMOUNT_KEY, { message: t(`transfer.investment_ach_transfer_error.${errorType}`) }) // Sets an inline error on the amount key
          break

        case CreateInvestmentAchTransferErrorType.TRANSFER_LIMIT_BREACHED:
        case CreateInvestmentAchTransferErrorType.BAD_REQUEST:
        case CreateInvestmentAchTransferErrorType.LINKED_ACCOUNT_ERROR:
        case CreateInvestmentAchTransferErrorType.TRANSFER_FAILED:
        case CreateInvestmentAchTransferErrorType.AMOUNT_REQUESTED_REQUIRES_LIQUIDATION:
          onError(errorType) // Sets a page error and will launch modal w/ the erorr content
          break

        case 'AnnualRetirementContributionExceededError':
          data.retirementContribution?.annualContributionsLimit &&
            onInlineError(AMOUNT_KEY, {
              message: t(`transfer.investment_ach_transfer_error.${errorType}`, {
                annualContributionLimit: Currency.integerCents.onlyMinus(
                  data.retirementContribution.annualContributionsLimit,
                ),
              }),
            }) // Sets an inline error on the amount key
          break

        default:
          log.error('Error Type not being handled', { errorType })
          break
      }
    }

    // SUCCESS SCENARIO
    if (createInvestmentAchTransfer.__typename === 'CreateInvestmentAchTransfer') {
      onSuccess(TransferSuccessTypes.InvestmentAch)
    } else {
      // Unhandled Fallback Scenarios
      log.warn('Unhandled Transfer Scenario')
    }
  }

  const { mutate: createInvestmentAchTransferMutation, isLoading: investmentAchTransferLoading } =
    useCreateInvestmentAchTransfer({
      onSuccess: onInvestmentAchTransferSuccess,
      onError: onInvestmentAchTransferError,
    })

  return {
    createInvestmentAchTransferMutation,
    investmentAchTransferLoading,
  }
}
